import { useCallback, useEffect, useMemo, useRef, useState, type SVGProps } from 'react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';

import { MapCard } from '../components/MapCard';
import { ParkingCameraAnnotator } from '../components/ParkingCameraAnnotator';
import { NoDataNotice } from '../components/NoDataNotice';
import { ParkingLayoutBuilder, type ParkingLayoutBuilderValue, type ParkingSpotType } from '../components/ParkingLayoutBuilder';
import parkingFeedImage from '../images/Parking Feed.png';
import { ParkingPaymentPanel, type ParkingPaymentParkingContext } from './ParkingPaymentPage';
import { callParkingBoxApi } from '../lib/parkingBoxApi';
import { useAuth } from '../hooks/useAuth';
import { getParkingLayoutRecord } from '../lib/parkingLayout';
import { parseParkingAreasResponse, pickFootprintFromAreas } from '../lib/parkingAreas';
import { getParkingData } from '../lib/parkingDataSource';
import { fetchLastUpdateInfo, type LastUpdateInfo } from '../lib/lastUpdate';
import { readStoredAuthToken } from '../lib/authTokens';
import { extractInternalParkingSnapshot } from '../lib/internalParkingData';
import { normalizeParkingListResponse, type ParkingListItem } from '../lib/parkingList';
import { normalizeParkingIdentifier, normalizeParkingUtilizationResponse } from '../lib/parkingUtilization';
import type { CameraPlacement } from '../lib/customParkingStorage';
import type { ParkingFootprint, ParkingLayoutRecord, ParkingSpotPricing, ParkingSpotWithChart } from '../lib/parkingTypes';

type CameraSnapshot = {
  camId: string;
  src: string;
  width: number;
  height: number;
};

type CameraViewSectionProps = {
  cameraId: string;
  cameraAreaName: string | null;
  cityId: string;
  spotId: string;
  isAdmin: boolean;
  layoutRecord: ParkingLayoutRecord | null;
  token: string | null;
};

type MobileDetailSection = 'camera' | 'layout' | 'analysis';

const parkingData = getParkingData();
const PARKING_FEED_IMAGE = {
  src: parkingFeedImage as string,
  width: 1122,
  height: 631,
};
const CAMERA_SNAPSHOT_REFRESH_SECONDS = 30;

function CameraViewSection({ cameraId, cameraAreaName, cityId, spotId, isAdmin, layoutRecord, token }: CameraViewSectionProps) {
  const [cameraSnapshot, setCameraSnapshot] = useState<CameraSnapshot | null>(null);
  const [cameraSnapshotError, setCameraSnapshotError] = useState<string | null>(null);
  const [isCameraSnapshotLoading, setIsCameraSnapshotLoading] = useState(false);
  const [refreshCountdown, setRefreshCountdown] = useState<number | null>(null);
  const hasCameraSnapshotRef = useRef(false);

  const fetchCameraSnapshot = useCallback(
    async (identifier: string, options?: { silent?: boolean }) => {
      const silent = options?.silent ?? false;
      if (!silent) {
        setIsCameraSnapshotLoading(true);
      }
      setCameraSnapshotError(null);
      try {
        const response = await callParkingBoxApi(
          {
            action: 'get-cam-snapshot',
            _data_: {
              cam_id: identifier,
            },
          },
          { jwtToken: token ?? undefined, useStoredToken: true }
        );
        const snapshotData = extractCameraSnapshotData(response.json);
        if (!snapshotData) {
          throw new Error('Brak danych obrazu.');
        }
        const src = normalizeCameraSnapshotSource(snapshotData);
        const { width, height } = await readSnapshotDimensions(src);
        setCameraSnapshot({
          camId: identifier,
          src,
          width,
          height,
        });
      } catch (snapshotError) {
        const message = snapshotError instanceof Error && snapshotError.message.length > 0 ? snapshotError.message : 'Nie udało się pobrać obrazu kamery.';
        if (!silent) {
          setCameraSnapshot(null);
        }
        setCameraSnapshotError(message);
      } finally {
        if (!silent) {
          setIsCameraSnapshotLoading(false);
        }
      }
    },
    [token]
  );

  useEffect(() => {
    hasCameraSnapshotRef.current = cameraSnapshot !== null;
  }, [cameraSnapshot]);

  useEffect(() => {
    if (!token) {
      setCameraSnapshot(null);
      setCameraSnapshotError(null);
      setRefreshCountdown(null);
      hasCameraSnapshotRef.current = false;
      return;
    }

    if (!cameraId) {
      setCameraSnapshot(null);
      setCameraSnapshotError(null);
      setRefreshCountdown(null);
      hasCameraSnapshotRef.current = false;
      return;
    }

    let isCancelled = false;
    let isFetchingSnapshot = false;
    let secondsLeft = CAMERA_SNAPSHOT_REFRESH_SECONDS;

    const triggerSnapshot = async () => {
      if (isCancelled || isFetchingSnapshot) {
        return;
      }
      isFetchingSnapshot = true;
      try {
        await fetchCameraSnapshot(cameraId, { silent: hasCameraSnapshotRef.current });
        hasCameraSnapshotRef.current = true;
      } finally {
        isFetchingSnapshot = false;
      }
    };

    setCameraSnapshotError(null);
    setRefreshCountdown(secondsLeft);
    void triggerSnapshot();

    const countdownId = window.setInterval(() => {
      if (isCancelled) {
        return;
      }
      secondsLeft = Math.max(0, secondsLeft - 1);
      setRefreshCountdown(secondsLeft);
      if (secondsLeft === 0) {
        secondsLeft = CAMERA_SNAPSHOT_REFRESH_SECONDS;
        void triggerSnapshot();
      }
    }, 1_000);

    return () => {
      isCancelled = true;
      window.clearInterval(countdownId);
    };
  }, [cameraId, fetchCameraSnapshot, token]);

  const cameraImage = useMemo(
    () =>
      cameraSnapshot
        ? {
            src: cameraSnapshot.src,
            name: cameraAreaName ? `Kamera ${cameraAreaName}` : `Kamera ${cameraSnapshot.camId}`,
            width: cameraSnapshot.width,
            height: cameraSnapshot.height,
          }
        : null,
    [cameraAreaName, cameraSnapshot]
  );

  return (
    <>
      {cameraId ? (
        <div className="pointer-events-none absolute right-4 top-4 z-10 flex items-center gap-2 rounded-full bg-white/90 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500 shadow-sm backdrop-blur dark:bg-neutral-900/70 dark:text-slate-200 max-[425px]:right-3 max-[425px]:top-3 max-[425px]:gap-1.5 max-[425px]:px-2 max-[425px]:py-0.5">
          <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-500" aria-hidden="true" />
          <span>Odświeżanie za {Math.max(0, refreshCountdown ?? CAMERA_SNAPSHOT_REFRESH_SECONDS)}s</span>
        </div>
      ) : null}

      {cameraImage ? (
        <ParkingCameraAnnotator cityId={cityId} spotId={spotId} isAdmin={isAdmin} initialRecord={layoutRecord ?? null} image={cameraImage} />
      ) : isCameraSnapshotLoading ? (
        <div className="flex min-h-[360px] flex-col items-center justify-center gap-3 p-10 text-center text-sm uppercase tracking-[0.35em] text-slate-500 dark:text-slate-300 max-[425px]:min-h-[260px] max-[425px]:gap-2 max-[425px]:p-6">
          <span className="h-10 w-10 animate-spin rounded-full border-4 border-emerald-500 border-t-transparent" aria-hidden="true" />
          <span>Ładowanie podglądu kamery...</span>
        </div>
      ) : (
        <div className="flex min-h-[360px] items-center justify-center p-10 text-center text-sm uppercase tracking-[0.35em] text-slate-500 dark:text-slate-300 max-[425px]:min-h-[260px] max-[425px]:p-6 max-[425px]:text-xs max-[425px]:tracking-[0.25em]">
          {cameraSnapshotError ?? 'Brak kamery przypisanej do tego parkingu.'}
        </div>
      )}
    </>
  );
}

type NumericPricingKey = Exclude<keyof ParkingSpotPricing, 'charging_period'>;

const pricingValueDefinitions: Array<{ label: string; keys: NumericPricingKey[] }> = [
  { label: 'Samochody osobowe', keys: ['passenger_cars'] },
  { label: 'Ciężarówki', keys: ['trucks'] },
  { label: 'Autobusy', keys: ['buses'] },
  { label: 'Motocykle', keys: ['moto_bikes'] },
  { label: 'Kampery', keys: ['kampery', 'campers'] },
  { label: 'Stacja ładowania', keys: ['charging_station'] },
];

const currencyFormatter = new Intl.NumberFormat('pl-PL', {
  style: 'currency',

  currency: 'PLN',

  maximumFractionDigits: 2,
});

const formatPrice = (value: number): string => currencyFormatter.format(value);

const truthyParam = (value: string | null) => {
  if (!value) {
    return false;
  }

  const normalized = value.toLowerCase();

  return normalized === '1' || normalized === 'true';
};

export default function ParkingDetailPage() {
  const { token, isAdmin: hasAdminAccess, userEmail } = useAuth();
  const [lastUpdate, setLastUpdate] = useState<LastUpdateInfo | null>(null);
  const [remoteSpot, setRemoteSpot] = useState<ParkingSpotWithChart | null>(null);
  const [remoteCityName, setRemoteCityName] = useState<string | null>(null);
  const [paymentParking, setPaymentParking] = useState<ParkingPaymentParkingContext | null>(null);
  const [remoteFootprint, setRemoteFootprint] = useState<ParkingFootprint | null>(null);
  const [remotePricing, setRemotePricing] = useState<ParkingSpotPricing | null>(null);
  const [pricingLabel, setPricingLabel] = useState<string | null>(null);
  const [parkingStats, setParkingStats] = useState<ParkingStats | null>(null);
  const [isParkingStatsUnavailable, setIsParkingStatsUnavailable] = useState(false);
  const [layoutRecord, setLayoutRecord] = useState<ParkingLayoutRecord | null>(null);
  const [layoutBuilder, setLayoutBuilder] = useState<ParkingLayoutBuilderValue | null>(null);
  const [activeView, setActiveView] = useState<'camera' | 'layout'>('camera');
  const [mobileSection, setMobileSection] = useState<MobileDetailSection | null>(null);
  const [isDesktopLayout, setIsDesktopLayout] = useState(() => {
    if (typeof window === 'undefined') {
      return true;
    }

    return window.matchMedia('(min-width: 768px)').matches;
  });
  const [isLayoutLoading, setIsLayoutLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isChartLoading, setIsChartLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [cameraIdentifier, setCameraIdentifier] = useState<string | null>(null);
  const [cameraAreaName, setCameraAreaName] = useState<string | null>(null);
  const detailsLoadKeyRef = useRef<string | null>(null);
  const hasLoadedDetailsRef = useRef(false);

  const { cityId = '', spotId = '' } = useParams<{ cityId?: string; spotId?: string }>();
  const location = useLocation();

  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const mediaQuery = window.matchMedia('(min-width: 768px)');
    const handleChange = (event: MediaQueryListEvent) => {
      setIsDesktopLayout(event.matches);
    };

    setIsDesktopLayout(mediaQuery.matches);

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleChange);
      return () => {
        mediaQuery.removeEventListener('change', handleChange);
      };
    }

    mediaQuery.addListener(handleChange);
    return () => {
      mediaQuery.removeListener(handleChange);
    };
  }, []);

  useEffect(() => {
    const storedToken = token ?? readStoredAuthToken();
    if (!storedToken) {
      setLastUpdate(null);
      return;
    }

    let isCancelled = false;

    const fetchLastUpdate = async () => {
      try {
        const result = await fetchLastUpdateInfo({ jwtToken: storedToken });
        if (!isCancelled) {
          setLastUpdate(result);
        }
      } catch (error) {
        if (!isCancelled) {
          console.error('[ParkingDetailPage] Nie udało się pobrać daty aktualizacji:', error);
          setLastUpdate(null);
        }
      }
    };

    fetchLastUpdate();

    return () => {
      isCancelled = true;
    };
  }, [token]);

  const isAdmin = useMemo(
    () => hasAdminAccess && (truthyParam(searchParams.get('admin')) || truthyParam(searchParams.get('edit'))),
    [hasAdminAccess, searchParams]
  );

  const queryCameraId = useMemo(() => {
    const param = searchParams.get('camId') ?? searchParams.get('cameraId') ?? searchParams.get('cam_id') ?? searchParams.get('cam');
    if (!param) {
      return null;
    }
    const normalized = param.trim();
    return normalized.length ? normalized : null;
  }, [searchParams]);

  const cityIdParam = decodeURIComponent(cityId).toLowerCase();

  const spotIdParam = decodeURIComponent(spotId).toLowerCase();

  useEffect(() => {
    setMobileSection(null);
  }, [cityIdParam, spotIdParam]);

  const fallbackCity = useMemo(
    () =>
      parkingData.cities.find((item) => item.id.toLowerCase() === cityIdParam) ??
      parkingData.cities.find((item) => item.id.toLowerCase() === parkingData.defaultCityId.toLowerCase()) ??
      null,
    [cityIdParam]
  );

  const fallbackSpot = useMemo(() => {
    if (!fallbackCity) {
      return null;
    }
    return fallbackCity.spots.find((item) => item.id.toLowerCase() === spotIdParam) ?? null;
  }, [fallbackCity, spotIdParam]);

  useEffect(() => {
    const fetchKey = `${cityIdParam}|${spotIdParam}|${queryCameraId ?? ''}`;

    if (!token) {
      setRemoteSpot(null);
      setPaymentParking(null);
      setRemoteFootprint(null);
      setRemotePricing(null);
      setPricingLabel(null);
      setParkingStats(null);
      setIsParkingStatsUnavailable(false);
      setCameraIdentifier(queryCameraId);
      setCameraAreaName(null);
      setIsLoading(false);
      detailsLoadKeyRef.current = null;
      hasLoadedDetailsRef.current = false;
      return;
    }

    if (hasLoadedDetailsRef.current && detailsLoadKeyRef.current === fetchKey) {
      return;
    }
    detailsLoadKeyRef.current = fetchKey;
    hasLoadedDetailsRef.current = false;

    let isCancelled = false;

    const fetchParkingDetails = async () => {
      setIsLoading(true);
      setLoadError(null);
      setCameraIdentifier(queryCameraId);
      setCameraAreaName(null);
      setPaymentParking(null);
      setParkingStats(null);
      setIsParkingStatsUnavailable(false);
      const detailRequests: Array<Promise<void>> = [];
      try {
        let resolvedCameraId = queryCameraId;
        let resolvedCameraAreaName: string | null = null;
        let resolvedCameraType: CameraPlacement | null = null;
        const areaNameById = new Map<string, string>();
        const listResponse = await callParkingBoxApi(
          {
            action: 'get-parking-list',
          },
          { jwtToken: token, useStoredToken: true }
        );
        if (isCancelled) {
          return;
        }

        const listItems = normalizeParkingListResponse(listResponse.json);
        const target = findParkingListMatch(listItems, spotIdParam);
        if (!target) {
          setLoadError('Nie znaleziono parkingu w Parking Box.');
          setPaymentParking(null);
          setRemoteSpot(null);
          return;
        }
        const mappedSpot = mapListItemToSpot(target, fallbackSpot);
        setRemoteSpot(mappedSpot);
        setRemoteCityName(target.city ?? fallbackCity?.name ?? null);
        setPaymentParking({
          id: target.id ?? mappedSpot.id,
          name: target.name ?? mappedSpot.label ?? mappedSpot.street,
          address: target.address ?? mappedSpot.street,
          city: target.city ?? fallbackCity?.id ?? cityIdParam,
          priceTemplateId: target.priceTemplateId ?? null,
        });
        setIsChartLoading(true);
        const chartPromise = fetchParkingUtilizationChart(token, target.id ?? mappedSpot.id)
          .then((chart) => {
            if (isCancelled) {
              return;
            }
            setRemoteSpot((prev) => (prev ? { ...prev, chart } : prev));
          })
          .finally(() => {
            if (!isCancelled) {
              setIsChartLoading(false);
            }
          });

        const pricingPromise = fetchParkingPricing(token, target.priceTemplateId).then((pricingResult) => {
          if (isCancelled) {
            return;
          }
          setRemotePricing(pricingResult.pricing);
          setPricingLabel(pricingResult.label);
        });

        detailRequests.push(chartPromise, pricingPromise);

        const resolvedParkingId = target.id ?? mappedSpot.id;
        if (resolvedParkingId !== null && typeof resolvedParkingId !== 'undefined') {
          try {
            const parkingAreaResponse = await callParkingBoxApi(
              {
                action: 'get-parking-area',
                _data_: { id: resolvedParkingId },
              },
              { jwtToken: token, useStoredToken: true }
            );
            if (isCancelled) {
              return;
            }
            console.log('[ParkingDetailPage] get-parking-area (id) response', parkingAreaResponse.json);
            const parsedAreas = parseParkingAreasResponse(parkingAreaResponse.json);
            parsedAreas.forEach((area) => areaNameById.set(area.id, area.name));
            const areaIds = parsedAreas.map((area) => area.id).filter((id): id is string => typeof id === 'string' && id.trim().length > 0);
            for (const areaId of areaIds) {
              try {
                const areaCamResponse = await callParkingBoxApi(
                  {
                    action: 'get-area-cam',
                    _data_: { area_id: areaId },
                  },
                  { jwtToken: token, useStoredToken: true }
                );
                if (isCancelled) {
                  return;
                }
                console.log('[ParkingDetailPage] get-area-cam response', { areaId, data: areaCamResponse.json });
                const extractedCam = extractAssignedCameraInfo(areaCamResponse.json);
                if (extractedCam) {
                  if (!resolvedCameraId) {
                    resolvedCameraId = extractedCam.camId;
                    resolvedCameraType = extractedCam.camType ?? resolvedCameraType;
                  }
                  if (resolvedCameraId === extractedCam.camId) {
                    const mappedAreaName =
                      extractedCam.areaId && areaNameById.has(extractedCam.areaId)
                        ? areaNameById.get(extractedCam.areaId) ?? null
                        : areaNameById.get(areaId) ?? null;
                    resolvedCameraAreaName = mappedAreaName ?? resolvedCameraAreaName;
                    resolvedCameraType = extractedCam.camType ?? resolvedCameraType;
                  } else if (!resolvedCameraType && extractedCam.camType) {
                    resolvedCameraType = extractedCam.camType;
                  }
                }
              } catch (areaCamError) {
                console.error(`[ParkingDetailPage] Nie udało się pobrać kamer dla obszaru ${areaId}:`, areaCamError);
              }
            }
          } catch (parkingError) {
            console.error('[ParkingDetailPage] Nie udało się pobrać danych parkingu (get-parking-area z id):', parkingError);
          }
          setIsLayoutLoading(true);
          try {
            const areasResponse = await callParkingBoxApi(
              {
                action: 'get-parking-area',
                _data_: { id: resolvedParkingId },
              },
              { jwtToken: token, useStoredToken: true }
            );
            if (isCancelled) {
              return;
            }
            const areas = parseParkingAreasResponse(areasResponse.json);
            areas.forEach((area) => areaNameById.set(area.id, area.name));
            const extractedAreasCam = extractAssignedCameraInfo(areasResponse.json);
            if (extractedAreasCam) {
              if (!resolvedCameraId) {
                resolvedCameraId = extractedAreasCam.camId;
                resolvedCameraType = extractedAreasCam.camType ?? resolvedCameraType;
              }
              if (resolvedCameraId === extractedAreasCam.camId) {
                resolvedCameraAreaName =
                  extractedAreasCam.areaId && areaNameById.has(extractedAreasCam.areaId)
                    ? areaNameById.get(extractedAreasCam.areaId) ?? resolvedCameraAreaName
                    : resolvedCameraAreaName;
                resolvedCameraType = extractedAreasCam.camType ?? resolvedCameraType;
              } else if (!resolvedCameraType && extractedAreasCam.camType) {
                resolvedCameraType = extractedAreasCam.camType;
              }
            }
            const footprint = pickFootprintFromAreas(areas);
            setRemoteFootprint(footprint);
            const primaryAreaId = areas[0]?.id ?? null;
            if (primaryAreaId) {
              try {
                const layoutResponse = await callParkingBoxApi(
                  {
                    action: 'get-parking-spots',
                    _data_: {
                      parking_id: resolvedParkingId,
                      area_id: primaryAreaId,
                    },
                  },
                  { jwtToken: token, useStoredToken: true }
                );
                console.log('[ParkingDetailPage] get-parking-spots response', layoutResponse.json);
                if (isCancelled) {
                  return;
                }
                const extractedLayoutCam = extractAssignedCameraInfo(layoutResponse.json);
                if (extractedLayoutCam) {
                  if (!resolvedCameraId) {
                    resolvedCameraId = extractedLayoutCam.camId;
                    resolvedCameraType = extractedLayoutCam.camType ?? resolvedCameraType;
                  }
                  if (resolvedCameraId === extractedLayoutCam.camId) {
                    resolvedCameraAreaName =
                      extractedLayoutCam.areaId && areaNameById.has(extractedLayoutCam.areaId)
                        ? areaNameById.get(extractedLayoutCam.areaId) ?? resolvedCameraAreaName
                        : resolvedCameraAreaName;
                    resolvedCameraType = extractedLayoutCam.camType ?? resolvedCameraType;
                  } else if (!resolvedCameraType && extractedLayoutCam.camType) {
                    resolvedCameraType = extractedLayoutCam.camType;
                  }
                }
                const builder = normalizeParkingLayoutBuilderValue(layoutResponse.json);
                console.log('[ParkingDetailPage] parsed builder', builder);
                if (builder) {
                  setLayoutBuilder(builder);
                  setLayoutRecord(builderToLayoutRecord(builder, cityIdParam || 'parking-city', mappedSpot.id));
                } else {
                  setLayoutBuilder(null);
                  setLayoutRecord(null);
                }
              } catch (layoutError) {
                console.error('[ParkingDetailPage] Nie udało się pobrać schematu parkingu:', layoutError);
                setLayoutBuilder(null);
                setLayoutRecord(null);
              }
            }
          } catch (areaError) {
            console.error('[ParkingDetailPage] Nie udało się pobrać obszarów parkingu:', areaError);
            setRemoteFootprint(null);
            setLayoutBuilder(null);
          } finally {
            if (!isCancelled) {
              setIsLayoutLoading(false);
            }
          }

          try {
            const statsResponse = await callParkingBoxApi(
              {
                action: 'get-parking-stats',
                _data_: { parking_id: resolvedParkingId },
              },
              { jwtToken: token, useStoredToken: true }
            );
            if (!isCancelled) {
              console.log('[ParkingDetailPage] get-parking-stats response', statsResponse.json);
              const payload = statsResponse.json;
              const statsUnavailableFlag =
                payload === false ||
                (payload &&
                  typeof payload === 'object' &&
                  ((payload as Record<string, unknown>)._data_ === false || (payload as Record<string, unknown>).data === false));
              if (statsUnavailableFlag) {
                setParkingStats(null);
                setIsParkingStatsUnavailable(true);
                return;
              }

              const parsedStats =
                resolvedCameraType === 'external'
                  ? normalizeExternalParkingStatsResponse(payload) ?? normalizeParkingStatsResponse(payload)
                  : normalizeParkingStatsResponse(payload) ?? normalizeExternalParkingStatsResponse(payload);
              if (!resolvedCameraType && parsedStats?.cameraType) {
                resolvedCameraType = parsedStats.cameraType;
              }
              setIsParkingStatsUnavailable(false);
              setParkingStats(parsedStats);
              if (parsedStats) {
                setRemoteSpot((prev) => {
                  if (!prev) {
                    return prev;
                  }
                  return {
                    ...prev,
                    occupied: parsedStats.occupied,
                    total: parsedStats.total,
                  };
                });
              }
            }
          } catch (statsError) {
            console.error('[ParkingDetailPage] Nie udało się pobrać statystyk parkingu:', statsError);
          }
        }
        await Promise.all(detailRequests);
        if (isCancelled) {
          return;
        }
        setCameraIdentifier(resolvedCameraId);
        setCameraAreaName(resolvedCameraAreaName);
        hasLoadedDetailsRef.current = true;
      } catch (error) {
        detailsLoadKeyRef.current = null;
        hasLoadedDetailsRef.current = false;
        if (isCancelled) {
          return;
        }
        const message = error instanceof Error && error.message.length ? error.message : 'Nie udało się pobrać danych parkingu.';
        setLoadError(message);
        setRemoteSpot(null);
        setLayoutBuilder(null);
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchParkingDetails();

    return () => {
      isCancelled = true;
    };
  }, [cityIdParam, fallbackCity, fallbackSpot, queryCameraId, spotIdParam, token]);

  const spot = remoteSpot ?? fallbackSpot;
  const cityName = remoteCityName ?? fallbackCity?.name ?? 'Brak nazwy miasta';
  const cityIdentifier = (fallbackCity?.id ?? cityIdParam) || 'parking-city';
  const targetCameraId = queryCameraId ?? cameraIdentifier;

  const resolvedLayoutRecord = useMemo(() => {
    if (!spot) {
      return null;
    }
    return layoutRecord ?? getParkingLayoutRecord(cityIdentifier, spot.id) ?? null;
  }, [cityIdentifier, layoutRecord, spot]);

  const city = { id: cityIdentifier, name: cityName };
  const paymentParkingContext = useMemo<ParkingPaymentParkingContext | null>(() => {
    if (paymentParking) {
      return paymentParking;
    }

    if (!spot) {
      return null;
    }

    return {
      id: spot.id,
      name: spot.label ?? spot.street,
      address: spot.street,
      city: city.id,
      priceTemplateId: null,
    };
  }, [city.id, paymentParking, spot]);
  const loginReturnPath = `${location.pathname}${location.search}`;
  const displayUserEmail = userEmail ?? 'Brak adresu e-mail';

  if (isLoading) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-slate-200 px-6 py-10 text-slate-900 transition-colors dark:bg-neutral-950 dark:text-slate-100">
        <div className="flex flex-col items-center gap-4 rounded-3xl border border-slate-200 bg-white px-6 py-8 text-center shadow-[0_20px_60px_-30px_rgba(15,23,42,0.15)] transition-colors dark:border-white/10 dark:bg-neutral-900/80">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-emerald-500 border-t-transparent" aria-label="loading" />
          <p className="text-xs uppercase tracking-[0.35em] text-slate-500 dark:text-slate-300/80">Ładowanie danych parkingu...</p>
        </div>
      </main>
    );
  }

  if (!spot && loadError) {
    return <NoDataNotice description={loadError} />;
  }

  if (!spot) {
    return <NoDataNotice description="Brak danych do wyświetlenia. Upewnij się, że parking istnieje w Parking Box." />;
  }

  const footprint = remoteFootprint ?? spot.footprint ?? null;

  const fallbackFreeSpots = Math.max(spot.total - spot.occupied, 0);
  const displayTotals = {
    total: parkingStats?.total ?? spot.total,
    free: isParkingStatsUnavailable ? '—' : parkingStats?.free ?? fallbackFreeSpots,
    occupied: isParkingStatsUnavailable ? '—' : parkingStats?.occupied ?? spot.occupied,
  };

  const chart = spot.chart ?? [];
  const maxChartValue = chart.length ? Math.max(...chart) : 0;
  const utilizationTicks = [100, 75, 50, 25, 0];
  const hourTicks = [0, 6, 12, 18, 23];
  const formatHourLabel = (hour: number) => `${hour}`;

  const editPolygonUrl = `/admin/parking/new?admin=1&cityId=${encodeURIComponent(city.id)}&spotId=${encodeURIComponent(spot.id)}`;

  const pricing = remotePricing ?? spot.pricing ?? null;

  const chargingPeriod = pricing?.charging_period?.trim() ?? '';

  const hasChargingPeriod = chargingPeriod.length > 0;

  const priceCards =
    pricing === null
      ? []
      : pricingValueDefinitions

          .map((definition) => {
            const numericValue = definition.keys

              .map((key) => pricing[key])

              .find((entry): entry is number => typeof entry === 'number');

            if (typeof numericValue !== 'number') {
              return null;
            }

            return {
              label: definition.label,

              value: numericValue,
            };
          })

          .filter((entry): entry is { label: string; value: number } => entry !== null);

  const displayLastUpdate: LastUpdateInfo = lastUpdate ?? {
    dateLabel: '--.--.----',
    timeLabel: '--:--',
  };

  const mobileSectionButtons: Array<{ id: MobileDetailSection; label: string }> = [
    { id: 'camera', label: 'Kamera' },
    { id: 'layout', label: 'Schemat' },
    { id: 'analysis', label: 'Analiza zajetosci' },
  ];

  const handleMobileSectionChange = (section: MobileDetailSection) => {
    setMobileSection((current) => (current === section ? null : section));
    if (section === 'camera' || section === 'layout') {
      setActiveView(section);
    }
  };

  const renderVisualSurface = (view: 'camera' | 'layout') => (
    <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white transition-colors dark:border-white/10 dark:bg-white/5 lg:flex-1">
      {view === 'camera' ? (
        targetCameraId ? (
          <CameraViewSection
            cameraId={targetCameraId}
            cameraAreaName={cameraAreaName}
            cityId={city.id}
            spotId={spot.id}
            isAdmin={isAdmin}
            layoutRecord={resolvedLayoutRecord ?? null}
            token={token ?? null}
          />
        ) : (
          <div className="flex min-h-[360px] items-center justify-center p-10 text-center text-sm uppercase tracking-[0.35em] text-slate-500 dark:text-slate-300 max-[425px]:min-h-[260px] max-[425px]:p-6 max-[425px]:text-xs max-[425px]:tracking-[0.25em]">
            Brak kamery przypisanej do tego parkingu.
          </div>
        )
      ) : layoutBuilder ? (
        <div className="p-4 max-[425px]:p-3">
          <ParkingLayoutBuilder
            value={layoutBuilder}
            spotOccupancy={parkingStats?.spotOccupancy}
            onChange={() => {
              /* read-only */
            }}
            disabled
            minimalView
          />
        </div>
      ) : (
        <p className="p-6 text-center text-sm text-slate-500 dark:text-slate-300/80 max-[425px]:p-4">Brak zapisanego schematu dla tego parkingu.</p>
      )}
    </div>
  );

  const renderPaymentPanel = (className?: string) => (
    <ParkingPaymentPanel
      className={className}
      fixedParking={paymentParkingContext}
      cityLabel={city.name}
      loginReturnPath={loginReturnPath}
      eyebrow="Oplac parking"
      heading=""
    />
  );

  const renderAnalysisCard = () => (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 transition-colors dark:border-white/10 dark:bg-white/5 max-[425px]:p-4">
      <div className="flex items-center justify-between text-[10px] font-semibold uppercase tracking-[0.35em] text-slate-400 dark:text-slate-300/80">
        <span>Analiza zajetosci</span>
        <span className="text-[11px] tracking-[0.2em] text-slate-600 dark:text-slate-200">{spot.street}</span>
      </div>
      <div className="relative mt-6 overflow-hidden rounded-2xl border border-slate-100 bg-white/60 shadow-inner dark:border-white/10 dark:bg-white/5 max-[425px]:mt-4 max-[425px]:rounded-xl">
        <div className="flex items-center justify-between px-5 pt-4 text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-400 dark:text-slate-300/80 max-[425px]:px-4 max-[425px]:pt-3">
          <span>Procent zajetosci</span>
        </div>
        <div className="mt-3 flex items-stretch gap-3 px-5 pb-6 max-[425px]:mt-2 max-[425px]:gap-2 max-[425px]:px-4 max-[425px]:pb-4">
          <div className="flex w-5 shrink-0 flex-col justify-between text-right text-[10px] text-slate-400 dark:text-slate-400/80">
            {utilizationTicks.map((tick) => (
              <span key={`detail-tick-${tick}`}>{tick}%</span>
            ))}
          </div>
          <div className="relative flex-1">
            <div className="pointer-events-none absolute inset-0 flex flex-col justify-between">
              {utilizationTicks.map((tick) => (
                <span key={`detail-grid-${tick}`} className="h-px w-full bg-slate-200/60 dark:bg-white/10" />
              ))}
            </div>
            <div className="relative flex h-[180px] items-end gap-[4px] sm:h-[200px]">
              {chart.map((value, index) => {
                const isPeak = value === maxChartValue && maxChartValue > 0;
                const hourLabel = formatHourLabel(index);
                return (
                  <div
                    key={`${spot.id}-chart-${index}`}
                    className={`flex-1 rounded-t transition-colors ${isPeak ? 'bg-emerald-500' : 'bg-slate-400 dark:bg-slate-600'}`}
                    style={{ height: `${value}%` }}
                    title={`${hourLabel} - ${value}% zajete`}
                  />
                );
              })}
            </div>
          </div>
        </div>
        <div className="px-5 pb-4 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400 dark:text-slate-400/80 max-[425px]:px-4 max-[425px]:pb-3">
          <div className="ml-[3.25rem] flex justify-between">
            {hourTicks.map((tick) => (
              <span key={`detail-hour-${tick}`}>{formatHourLabel(tick)}</span>
            ))}
          </div>
        </div>
        {isChartLoading ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 rounded-2xl bg-white/85 text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-500 backdrop-blur-sm dark:bg-neutral-900/80 dark:text-slate-200">
            <span
              className="h-10 w-10 animate-spin rounded-full border-2 border-emerald-200 border-t-emerald-500 dark:border-emerald-900 dark:border-t-emerald-400"
              aria-hidden="true"
            />
            <span>Ladowanie</span>
          </div>
        ) : null}
      </div>
      <p className="mt-3 text-[11px] leading-relaxed text-slate-500 dark:text-slate-300/80">Ostatnie 24 godziny ruchu.</p>
    </div>
  );

  const renderMobileAvailabilityCard = () => (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 text-center transition-colors dark:border-white/10 dark:bg-white/5 max-[425px]:rounded-[20px] max-[425px]:p-3.5">
      <p className="text-[11px] uppercase tracking-[0.45em] text-slate-400 dark:text-slate-300/80">Parking</p>
      <h1 className="mt-2 text-xl font-semibold uppercase tracking-[0.18em] text-slate-700 dark:text-slate-100 max-[425px]:text-lg max-[425px]:tracking-[0.14em]">
        {spot.street} <span className="text-emerald-500">{city.name}</span>
      </h1>
      {isLoading ? (
        <p className="mt-2 text-[11px] uppercase tracking-[0.3em] text-slate-500 dark:text-slate-300/80">Odswiezam dane z Parking Box...</p>
      ) : null}
      <div className="mt-4 grid grid-cols-2 gap-3">
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-center dark:border-emerald-400/40 dark:bg-emerald-400/10">
          <p className="text-[10px] uppercase tracking-[0.4em] text-emerald-600 dark:text-emerald-200">Wolne</p>
          <p className="mt-2 text-3xl font-semibold text-emerald-600 dark:text-emerald-200">{displayTotals.free}</p>
        </div>
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-center dark:border-rose-400/40 dark:bg-rose-400/10">
          <p className="text-[10px] uppercase tracking-[0.4em] text-rose-500">Zajete</p>
          <p className="mt-2 text-3xl font-semibold text-rose-500">{displayTotals.occupied}</p>
        </div>
      </div>
    </div>
  );

  return (
    <main className="min-h-screen bg-slate-200 px-5 py-9 text-slate-900 transition-colors dark:bg-neutral-950 dark:text-slate-100 max-lg:px-4 max-lg:py-7 max-md:px-3 max-md:py-6 max-sm:px-3 max-sm:py-5 max-[425px]:px-2 max-[425px]:py-5">
      <div className="mx-auto flex w-full max-w-[1920px] flex-col gap-10 overflow-hidden rounded-[32px] bg-white shadow-[0_30px_80px_-40px_rgba(15,23,42,0.45)] transition-colors dark:bg-neutral-900/80 dark:shadow-[0_30px_80px_-40px_rgba(0,0,0,0.6)] max-lg:gap-8 max-lg:rounded-[28px] max-md:gap-0 max-md:rounded-[26px] max-sm:rounded-[24px] max-[425px]:rounded-[22px]">
        <header className="flex flex-col gap-6 border-b border-slate-200 px-5 py-7 transition-colors dark:border-white/10 max-lg:gap-5 max-lg:px-4 max-lg:py-6 max-md:gap-4 max-md:px-3 max-md:py-5 max-sm:gap-3 max-sm:px-2.5 max-sm:py-4 max-[425px]:gap-4 max-[425px]:px-3 max-[425px]:py-5 min-[1280px]:flex-row min-[1280px]:items-center min-[1280px]:justify-between">
          <div className="flex flex-wrap items-center gap-6 max-lg:gap-5 max-md:w-full max-md:gap-4 max-sm:gap-3 max-[425px]:gap-4">
            <Link
              to="/"
              className="rounded-full border border-slate-200 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-slate-600 transition hover:border-emerald-500 hover:text-emerald-600 dark:border-white/10 dark:text-slate-200 dark:hover:border-emerald-400 dark:hover:text-emerald-300 max-md:px-3.5 max-md:py-1.5 max-sm:px-3 max-sm:py-1.5"
            >
              Powrót
            </Link>

            <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.4em] max-[1440px]:text-xs max-[1440px]:tracking-[0.3em] max-md:text-xs max-md:tracking-[0.25em] max-sm:text-[11px] max-sm:tracking-[0.2em]">
              <span className="rounded-lg bg-emerald-500 px-3 py-1 text-white max-md:px-2.5 max-md:py-0.5 max-sm:px-2">Parking</span>

              <span className="rounded-lg border border-emerald-500 px-3 py-1 text-emerald-600 dark:border-emerald-400 dark:text-emerald-300 max-md:px-2.5 max-md:py-0.5 max-sm:px-2">Box</span>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-6 max-lg:gap-5 max-md:w-full max-md:justify-between max-md:gap-4 max-sm:gap-3 max-[425px]:gap-4">
            <div className="text-right text-xs uppercase tracking-[0.35em] text-slate-400 dark:text-slate-300/80 max-[1440px]:text-[11px] max-[1440px]:tracking-[0.3em] max-md:text-left max-md:text-[10px] max-md:tracking-[0.25em] max-sm:text-[9px] max-sm:tracking-[0.2em]">
              <p>Odczyt danych</p>

              <p className="mt-1 text-sm tracking-normal text-slate-700 dark:text-slate-100 max-[1440px]:text-[0.8rem] max-md:text-xs max-sm:text-[11px]">
                {displayLastUpdate.dateLabel} <span className="text-slate-400 dark:text-slate-400/70">{displayLastUpdate.timeLabel}</span>
              </p>
            </div>

            <span className="hidden" />

            <div className="hidden">
              <div className="hidden h-11 w-11 items-center justify-center rounded-full bg-slate-100 text-slate-500 transition-colors dark:bg-white/10 dark:text-slate-200 min-[480px]:flex">
                <UserIcon className="h-5 w-5" />
              </div>
              <div className="text-right text-xs uppercase tracking-[0.35em] text-slate-400 dark:text-slate-300/80 max-[1440px]:text-[11px] max-[1440px]:tracking-[0.3em] max-md:text-right max-md:text-[10px] max-md:tracking-[0.25em] max-sm:text-[9px] max-sm:tracking-[0.2em]">
                <p>Uzytkownik</p>
                <p className="mt-1 max-w-[12rem] truncate text-sm tracking-normal text-slate-700 dark:text-slate-100 max-[1440px]:text-[0.8rem] max-md:max-w-[9rem] max-md:text-xs max-sm:max-w-[7.5rem] max-sm:text-[11px]">
                  {displayUserEmail}
                </p>
              </div>
            </div>

            <div className="hidden">
              <div className="hidden h-11 w-11 items-center justify-center rounded-full bg-slate-100 text-slate-500 transition-colors dark:bg-white/10 dark:text-slate-200 min-[480px]:flex">
                <UserIcon className="h-5 w-5" />
              </div>
              <div className="text-right text-xs uppercase tracking-[0.35em] text-slate-400 dark:text-slate-300/80 max-[1440px]:text-[11px] max-[1440px]:tracking-[0.3em] max-md:text-right max-md:text-[10px] max-md:tracking-[0.25em] max-sm:text-[9px] max-sm:tracking-[0.2em]">
              <p>Użytkownik</p>

              <p className="mt-1 text-sm tracking-normal text-slate-700 dark:text-slate-100">Użytkownik</p>
            </div>
            </div>
          </div>
        </header>

        <section className="flex flex-col gap-8 border-b-0 border-slate-200 px-6 transition-colors dark:border-white/10 max-lg:gap-7 max-lg:px-4 max-lg:py-6 max-md:gap-6 max-md:border-b max-md:px-3 max-md:py-5 max-sm:gap-5 max-sm:px-2.5 max-sm:py-4 max-[425px]:gap-4 max-[425px]:px-2.5 max-[425px]:py-3.5">
          <div className="flex flex-col gap-6 rounded-3xl border border-slate-200 bg-white p-8 shadow-[0_20px_60px_-30px_rgba(15,23,42,0.15)] transition-colors dark:border-white/10 dark:bg-neutral-900/80 max-lg:p-6 max-md:rounded-[26px] max-md:p-5 max-sm:rounded-[24px] max-sm:p-4 max-[425px]:gap-3 max-[425px]:rounded-[22px] max-[425px]:p-3.5">
            {isDesktopLayout ? (
              <div className="grid gap-4 lg:grid-cols-[minmax(0,1.45fr)_minmax(340px,420px)] lg:items-stretch">
              <div className="flex min-w-0 flex-col gap-4 lg:h-full">
                <div className="flex flex-wrap items-center justify-between gap-3 text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-500 dark:text-slate-300/80 max-[425px]:gap-2">
              <span>Podglad</span>
              <div className="flex items-center gap-3">
                {activeView === 'layout' && isLayoutLoading ? (
                  <span className="text-[10px] uppercase tracking-[0.25em] text-slate-400 dark:text-slate-300/80">Ładowanie schematu...</span>
                ) : null}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setActiveView('camera')}
                    className={`rounded-full border px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.3em] transition max-[425px]:px-3 max-[425px]:py-1.5 ${
                      activeView === 'camera'
                        ? 'border-emerald-500 bg-emerald-500 text-white'
                        : 'border-slate-300 text-slate-600 hover:border-emerald-400 hover:text-emerald-600 dark:border-white/15 dark:text-slate-200 dark:hover:border-emerald-400'
                    }`}
                  >
                    Widok kamery
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveView('layout')}
                    className={`rounded-full border px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.3em] transition max-[425px]:px-3 max-[425px]:py-1.5 ${
                      activeView === 'layout'
                        ? 'border-emerald-500 bg-emerald-500 text-white'
                        : 'border-slate-300 text-slate-600 hover:border-emerald-400 hover:text-emerald-600 dark:border-white/15 dark:text-slate-200 dark:hover:border-emerald-400'
                    }`}
                  >
                    Schemat
                  </button>
                </div>
              </div>
            </div>

            <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white transition-colors dark:border-white/10 dark:bg-white/5 lg:flex-1">
              {activeView === 'camera' ? (
                targetCameraId ? (
                  <CameraViewSection
                    cameraId={targetCameraId}
                    cameraAreaName={cameraAreaName}
                    cityId={city.id}
                    spotId={spot.id}
                    isAdmin={isAdmin}
                    layoutRecord={resolvedLayoutRecord ?? null}
                    token={token ?? null}
                  />
                ) : (
                  <div className="flex min-h-[360px] items-center justify-center p-10 text-center text-sm uppercase tracking-[0.35em] text-slate-500 dark:text-slate-300 max-[425px]:min-h-[260px] max-[425px]:p-6 max-[425px]:text-xs max-[425px]:tracking-[0.25em]">
                    Brak kamery przypisanej do tego parkingu.
                  </div>
                )
              ) : layoutBuilder ? (
                <div className="p-4 max-[425px]:p-3">
                  <ParkingLayoutBuilder
                    value={layoutBuilder}
                    spotOccupancy={parkingStats?.spotOccupancy}
                    onChange={() => {
                      /* read-only */
                    }}
                    disabled
                    minimalView
                  />
                </div>
              ) : (
                <p className="p-6 text-center text-sm text-slate-500 dark:text-slate-300/80 max-[425px]:p-4">Brak zapisanego schematu dla tego parkingu.</p>
              )}
            </div>
              </div>

              <div className="min-w-0 lg:flex lg:h-full">
                <ParkingPaymentPanel
                  className="lg:flex-1"
                  fixedParking={paymentParkingContext}
                  cityLabel={city.name}
                  loginReturnPath={loginReturnPath}
                  eyebrow="Opłać parking"
                  heading=""
                />
              </div>
              </div>
            ) : (
              <div className="flex flex-col gap-4 md:hidden">
                {renderMobileAvailabilityCard()}

                {renderPaymentPanel()}

                <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-3 transition-colors dark:border-white/10 dark:bg-white/5 max-[425px]:rounded-[20px]">
                  <div className="flex items-center justify-between gap-3 text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-500 dark:text-slate-300/80">
                    <span>Sekcje</span>
                    {mobileSection === 'layout' && isLayoutLoading ? (
                      <span className="text-[10px] uppercase tracking-[0.25em] text-slate-400 dark:text-slate-300/80">Ladowanie schematu...</span>
                    ) : null}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {mobileSectionButtons.map((section) => (
                      <button
                        key={section.id}
                        type="button"
                        onClick={() => handleMobileSectionChange(section.id)}
                        className={`rounded-full border px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.26em] transition max-[425px]:px-3 max-[425px]:py-1.5 ${
                          mobileSection === section.id
                            ? 'border-emerald-500 bg-emerald-500 text-white'
                            : 'border-slate-300 bg-white text-slate-600 hover:border-emerald-400 hover:text-emerald-600 dark:border-white/15 dark:bg-transparent dark:text-slate-200 dark:hover:border-emerald-400'
                        }`}
                      >
                        {section.label}
                      </button>
                    ))}
                  </div>
                </div>

                {mobileSection === 'camera' || mobileSection === 'layout' ? (
                  <div className="flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-[0_20px_60px_-30px_rgba(15,23,42,0.15)] transition-colors dark:border-white/10 dark:bg-white/5 max-[425px]:rounded-[20px] max-[425px]:p-3.5">
                    <div className="flex items-center justify-between gap-3 text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-500 dark:text-slate-300/80">
                      <span>{mobileSection === 'camera' ? 'Widok kamery' : 'Schemat parkingu'}</span>
                      {mobileSection === 'layout' && isLayoutLoading ? (
                        <span className="text-[10px] uppercase tracking-[0.25em] text-slate-400 dark:text-slate-300/80">Ladowanie schematu...</span>
                      ) : null}
                    </div>
                    {renderVisualSurface(mobileSection)}
                  </div>
                ) : null}

                {mobileSection === 'analysis' ? renderAnalysisCard() : null}
              </div>
            )}

            <div className="grid gap-4 lg:grid-cols-3 max-[425px]:gap-3">
              <div className="max-md:hidden rounded-2xl border border-slate-200 bg-white p-6 text-center transition-colors dark:border-white/10 dark:bg-white/5 max-[425px]:p-4">
                <p className="text-[11px] uppercase tracking-[0.45em] text-slate-400 dark:text-slate-300/80">Parking</p>
                <h1 className="mt-2 text-2xl font-semibold uppercase tracking-[0.2em] text-slate-700 dark:text-slate-100">
                  {spot.street} <span className="text-emerald-500">{city.name}</span>
                </h1>
                {isLoading ? (
                  <p className="mt-2 text-[11px] uppercase tracking-[0.3em] text-slate-500 dark:text-slate-300/80">Odświeżam dane z Parking Box...</p>
                ) : null}
                <div className="mt-6 grid grid-cols-3 gap-4 max-[425px]:mt-4 max-[425px]:grid-cols-2 max-[425px]:gap-3">
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-center dark:border-white/10 dark:bg-white/5 max-[425px]:p-2">
                    <p className="text-[10px] uppercase tracking-[0.4em] text-slate-400 dark:text-slate-300/80">Wszystkie</p>
                    <p className="mt-2 text-3xl font-semibold text-slate-800 dark:text-slate-100">{displayTotals.total}</p>
                  </div>
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-center dark:border-emerald-400/40 dark:bg-emerald-400/10 max-[425px]:p-2">
                    <p className="text-[10px] uppercase tracking-[0.4em] text-emerald-600 dark:text-emerald-200">Wolne</p>
                    <p className="mt-2 text-3xl font-semibold text-emerald-600 dark:text-emerald-200">{displayTotals.free}</p>
                  </div>
                  <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-center dark:border-rose-400/40 dark:bg-rose-400/10 max-[425px]:p-2">
                    <p className="text-[10px] uppercase tracking-[0.4em] text-rose-500">Zajęte</p>
                    <p className="mt-2 text-3xl font-semibold text-rose-500">{displayTotals.occupied}</p>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-6 transition-colors dark:border-white/10 dark:bg-white/5 max-[425px]:p-4">
                <div className="flex items-center justify-between text-[10px] font-semibold uppercase tracking-[0.35em] text-slate-400 dark:text-slate-300/80">
                  <span>Cennik</span>
                  {pricingLabel ? <span className="text-[10px] tracking-[0.25em] text-emerald-600 dark:text-emerald-300">{pricingLabel}</span> : null}
                  {hasChargingPeriod ? (
                    <span className="rounded-full border border-emerald-500 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.25em] text-emerald-600 dark:border-emerald-400 dark:text-emerald-200">
                      {chargingPeriod}
                    </span>
                  ) : null}
                </div>
                {priceCards.length > 0 ? (
                  <ul className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-1 2xl:grid-cols-2">
                    {priceCards.map((item) => (
                      <li
                        key={item.label}
                        className="flex items-center justify-between gap-2 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-100 max-[425px]:px-2 max-[425px]:py-1.5 lg:flex-col lg:items-start lg:justify-start lg:gap-1 2xl:flex-row 2xl:items-center 2xl:justify-between"
                      >
                        <span className="min-w-0 flex-1 text-[10px] tracking-[0.25em] text-slate-500 dark:text-slate-300/80">
                          {item.label}
                        </span>
                        <span className="shrink-0 text-sm text-slate-800 dark:text-slate-100">{formatPrice(item.value)}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-4 text-sm text-slate-500 dark:text-slate-300/80">Brak danych o cenach.</p>
                )}
              </div>

              <div className="max-md:hidden rounded-2xl border border-slate-200 bg-white p-6 transition-colors dark:border-white/10 dark:bg-white/5 max-[425px]:p-4">
                <div className="flex items-center justify-between text-[10px] font-semibold uppercase tracking-[0.35em] text-slate-400 dark:text-slate-300/80">
                  <span>Analiza zajętości</span>
                  <span className="text-[11px] tracking-[0.2em] text-slate-600 dark:text-slate-200">{spot.street}</span>
                </div>
                <div className="relative mt-6 overflow-hidden rounded-2xl border border-slate-100 bg-white/60 shadow-inner dark:border-white/10 dark:bg-white/5 max-[425px]:mt-4 max-[425px]:rounded-xl">
                  <div className="flex items-center justify-between px-5 pt-4 text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-400 dark:text-slate-300/80 max-[425px]:px-4 max-[425px]:pt-3">
                    <span>Procent zajętości</span>
                  </div>
                  <div className="mt-3 flex items-stretch gap-3 px-5 pb-6 max-[425px]:mt-2 max-[425px]:gap-2 max-[425px]:px-4 max-[425px]:pb-4">
                    <div className="flex w-5 shrink-0 flex-col justify-between text-right text-[10px] text-slate-400 dark:text-slate-400/80">
                      {utilizationTicks.map((tick) => (
                        <span key={`detail-tick-${tick}`}>{tick}%</span>
                      ))}
                    </div>
                    <div className="relative flex-1">
                      <div className="pointer-events-none absolute inset-0 flex flex-col justify-between">
                        {utilizationTicks.map((tick) => (
                          <span key={`detail-grid-${tick}`} className="h-px w-full bg-slate-200/60 dark:bg-white/10" />
                        ))}
                      </div>
                      <div className="relative flex h-[180px] items-end gap-[4px] sm:h-[200px]">
                        {chart.map((value, index) => {
                          const isPeak = value === maxChartValue && maxChartValue > 0;
                          const hourLabel = formatHourLabel(index);
                          return (
                            <div
                              key={`${spot.id}-chart-${index}`}
                              className={`flex-1 rounded-t transition-colors ${isPeak ? 'bg-emerald-500' : 'bg-slate-400 dark:bg-slate-600'}`}
                              style={{ height: `${value}%` }}
                              title={`${hourLabel} — ${value}% zajęte`}
                            />
                          );
                        })}
                      </div>
                    </div>
                  </div>
                  <div className="px-5 pb-4 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400 dark:text-slate-400/80 max-[425px]:px-4 max-[425px]:pb-3">
                    <div className="ml-[3.25rem] flex justify-between">
                      {hourTicks.map((tick) => (
                        <span key={`detail-hour-${tick}`}>{formatHourLabel(tick)}</span>
                      ))}
                    </div>
                  </div>
                  {isChartLoading ? (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 rounded-2xl bg-white/85 text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-500 backdrop-blur-sm dark:bg-neutral-900/80 dark:text-slate-200">
                      <span
                        className="h-10 w-10 animate-spin rounded-full border-2 border-emerald-200 border-t-emerald-500 dark:border-emerald-900 dark:border-t-emerald-400"
                        aria-hidden="true"
                      />
                      <span>Ładowanie</span>
                    </div>
                  ) : null}
                </div>
                <p className="mt-3 text-[11px] leading-relaxed text-slate-500 dark:text-slate-300/80">Ostatnie 24 godziny ruchu.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="flex flex-col gap-6 px-6 pb-10 max-lg:gap-5 max-lg:px-4 max-lg:py-6 max-md:gap-4 max-md:px-3 max-md:py-5 max-sm:gap-4 max-sm:px-2.5 max-sm:py-4 max-[425px]:gap-5 max-[425px]:px-3 max-[425px]:py-5">
          <div className="flex flex-wrap items-center justify-between text-xs font-semibold uppercase tracking-[0.4em] text-slate-400 dark:text-slate-300/80">
            <span>Lokalizacja</span>

            <span className="text-[11px] tracking-[0.2em] text-slate-600 dark:text-slate-200">{city.name}</span>
          </div>

          <div className="relative h-[420px] overflow-hidden rounded-[32px] border border-slate-200 transition-colors dark:border-white/10 md:h-[460px] max-[425px]:h-[320px] max-[425px]:rounded-[24px]">
            {isAdmin ? (
              <Link
                to={editPolygonUrl}
                className="absolute right-4 top-4 z-10 rounded-xl border border-emerald-500 bg-white/80 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-emerald-600 transition hover:bg-emerald-500 hover:text-white dark:border-emerald-400 dark:bg-neutral-900/80 dark:text-emerald-300 dark:hover:bg-emerald-400/20 max-[425px]:right-3 max-[425px]:top-3 max-[425px]:px-3 max-[425px]:py-1.5"
              >
                Edytuj poligon
              </Link>
            ) : null}

            <MapCard
              markers={[
                {
                  id: spot.id,

                  label: spot.label,

                  lngLat: spot.coords,

                  street: spot.street,

                  variant: 'highlight',

                  footprint: footprint ?? undefined,
                },
              ]}
              className="h-full w-full"
            />
          </div>
        </section>
      </div>
    </main>
  );
}

function UserIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" strokeWidth={1.8} stroke="currentColor" aria-hidden="true" {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 7.5a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 19.125a7.125 7.125 0 0114.25 0V21H4.5v-1.875z" />
    </svg>
  );
}

const extractCameraSnapshotData = (payload: unknown): string | null => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  if ('_data_' in payload) {
    const data = (payload as Record<string, unknown>)['_data_'];
    if (typeof data === 'string' && data.trim().length > 0) {
      return data.trim();
    }
  }

  if ('data' in payload) {
    const data = (payload as Record<string, unknown>).data;
    if (typeof data === 'string' && data.trim().length > 0) {
      return data.trim();
    }
  }

  return null;
};

const normalizeCameraSnapshotSource = (raw: string): string => {
  const trimmed = raw.trim();
  if (trimmed.startsWith('data:image/')) {
    return trimmed;
  }
  const sanitized = trimmed.replace(/\s+/g, '');
  return `data:image/jpeg;base64,${sanitized}`;
};

const readSnapshotDimensions = (src: string): Promise<{ width: number; height: number }> =>
  new Promise((resolve) => {
    if (typeof window === 'undefined' || typeof window.Image === 'undefined') {
      resolve({ width: PARKING_FEED_IMAGE.width, height: PARKING_FEED_IMAGE.height });
      return;
    }
    const img = new window.Image();
    img.onload = () => {
      resolve({
        width: img.naturalWidth || PARKING_FEED_IMAGE.width,
        height: img.naturalHeight || PARKING_FEED_IMAGE.height,
      });
    };
    img.onerror = () => {
      resolve({ width: PARKING_FEED_IMAGE.width, height: PARKING_FEED_IMAGE.height });
    };
    img.src = src;
  });

const normalizeCameraPlacement = (value: unknown): CameraPlacement | null => {
  if (typeof value !== 'string') {
    return null;
  }
  const normalized = value.trim().toLowerCase();
  if (normalized === 'external') {
    return 'external';
  }
  if (normalized === 'internal') {
    return 'internal';
  }
  return null;
};

type CameraAssignment = { camId: string; areaId: string | null; camType: CameraPlacement | null };

const normalizeCameraAssignmentValue = (value: unknown): string | null => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length ? trimmed : null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return null;
};

const extractAssignedCameraInfo = (payload: unknown): CameraAssignment | null => {
  if (!payload) {
    return null;
  }
  const tryNormalize = (record: Record<string, unknown>): CameraAssignment | null => {
    const camId = normalizeCameraAssignmentValue(record.cam_id ?? record.id ?? record.camId);
    if (!camId) {
      return null;
    }
    const areaId = normalizeCameraAssignmentValue(record.area_id ?? record.areaId ?? record.area ?? record.areaID);
    const camType = normalizeCameraPlacement(record.cam_type ?? record.type ?? record.camType ?? record.camera_type);
    return { camId, areaId, camType };
  };
  if (Array.isArray(payload)) {
    for (const entry of payload) {
      if (entry && typeof entry === 'object') {
        const normalized = tryNormalize(entry as Record<string, unknown>);
        if (normalized) {
          return normalized;
        }
      }
    }
  }
  if (payload && typeof payload === 'object') {
    const record = payload as Record<string, unknown>;
    const normalized = tryNormalize(record);
    if (normalized) {
      return normalized;
    }
    const nestedCandidates = [record.data, record._data_, record.response, record.result, record.payload];
    for (const nested of nestedCandidates) {
      if (nested && typeof nested === 'object') {
        const extracted = extractAssignedCameraInfo(nested);
        if (extracted) {
          return extracted;
        }
      }
    }
  }
  return null;
};

const extractAssignedCameraId = (payload: unknown): string | null => extractAssignedCameraInfo(payload)?.camId ?? null;

const DEFAULT_MARKER_COORDS: [number, number] = [19.1451, 52.2159];
const DEFAULT_CHART_TEMPLATE = Array.from({ length: 24 }, () => 0);

type PricingTemplate = {
  id: string;
  name: string | null;
  charging_period?: string | null;
  passenger_cars?: string | number | null;
  trucks?: string | number | null;
  buses?: string | number | null;
  moto_bikes?: string | number | null;
  campers?: string | number | null;
  charging_station?: string | number | null;
};

type BuilderSpot = ParkingLayoutBuilderValue['spots'][number];
type BuilderGate = ParkingLayoutBuilderValue['gates'][number];

const fetchParkingUtilizationChart = async (token: string, parkingId: string | number | null | undefined): Promise<number[]> => {
  const resolvedId = normalizeParkingIdentifier(parkingId);
  if (!resolvedId) {
    return [...DEFAULT_CHART_TEMPLATE];
  }
  try {
    const response = await callParkingBoxApi(
      {
        action: 'get-parking-utilization',
        _data_: { parking_id: resolvedId },
      },
      { jwtToken: token, useStoredToken: true }
    );
    const chart = normalizeParkingUtilizationResponse(response.json);
    return chart.length ? chart : [...DEFAULT_CHART_TEMPLATE];
  } catch (error) {
    console.error('[ParkingDetailPage] Nie udało się pobrać analizy zajętości:', error);
    return [...DEFAULT_CHART_TEMPLATE];
  }
};

const normalizeIdentifier = (value: unknown): string | null => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length ? trimmed.toLowerCase() : null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return null;
};

const normalizeDisplayId = (value: unknown, fallback: string): string => {
  const normalized = normalizeIdentifier(value);
  if (normalized !== null) {
    return normalized;
  }
  return fallback;
};

const findParkingListMatch = (items: ParkingListItem[], targetId: string): ParkingListItem | null => {
  if (items.length === 0) {
    return null;
  }
  const normalizedTarget = targetId.trim().toLowerCase();
  const directMatch =
    normalizedTarget.length === 0
      ? null
      : items.find((item) => normalizeIdentifier(item.id) === normalizedTarget || normalizeIdentifier(item.name) === normalizedTarget);
  if (directMatch) {
    return directMatch;
  }
  return items[0] ?? null;
};

const parseCoords = (value: [number, number] | null | undefined, fallback: [number, number]): [number, number] => {
  if (Array.isArray(value) && value.length >= 2) {
    const lng = Number(value[0]);
    const lat = Number(value[1]);
    if (Number.isFinite(lng) && Number.isFinite(lat)) {
      return [lng, lat];
    }
  }
  return fallback;
};

const cloneChart = (chart?: number[]): number[] => {
  if (Array.isArray(chart) && chart.length > 0) {
    return [...chart];
  }
  return [...DEFAULT_CHART_TEMPLATE];
};

const mapListItemToSpot = (item: ParkingListItem, fallback: ParkingSpotWithChart | null): ParkingSpotWithChart => {
  const fallbackId = fallback?.id ?? 'parking';
  const normalizedId = normalizeDisplayId(item.id, fallbackId);
  const label = item.name ?? fallback?.label ?? `Parking ${normalizedId}`;
  const street = item.address ?? fallback?.street ?? label;
  const stats = item.stats ?? null;
  const statsTotal = stats?.total ?? (stats && stats.occupied !== null && stats.free !== null ? stats.occupied + stats.free : null);
  const statsOccupied = stats?.occupied ?? (stats && stats.total !== null && stats.free !== null ? Math.max(stats.total - stats.free, 0) : null);
  const total = Math.max(0, Math.round(statsTotal ?? item.space_regular ?? fallback?.total ?? 0));
  const occupied = Math.max(0, Math.round(statsOccupied ?? fallback?.occupied ?? 0));
  const coords = parseCoords(item.coords as [number, number] | null, fallback?.coords ?? DEFAULT_MARKER_COORDS);

  return {
    id: normalizedId,
    street,
    label,
    total,
    occupied,
    coords,
    footprint: fallback?.footprint,
    pricing: fallback?.pricing,
    chart: cloneChart(fallback?.chart),
  };
};

const parsePriceNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().replace(',', '.');
    if (!normalized.length) {
      return null;
    }
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const normalizePricingTemplate = (entry: unknown): PricingTemplate | null => {
  if (!entry || typeof entry !== 'object') {
    return null;
  }
  const record = entry as Record<string, unknown>;
  const id = normalizeIdentifier(record.id ?? record.price_id);
  if (!id) {
    return null;
  }
  const name = typeof record.name === 'string' && record.name.trim().length > 0 ? record.name.trim() : null;
  return {
    id,
    name,
    charging_period: typeof record.charging_period === 'string' ? record.charging_period : null,
    passenger_cars: record.passenger_cars as string | number | null,
    trucks: record.trucks as string | number | null,
    buses: record.buses as string | number | null,
    moto_bikes: record.moto_bikes as string | number | null,
    campers: record.campers as string | number | null,
    charging_station: record.charging_station as string | number | null,
  };
};

const extractPricingArray = (payload: unknown, depth = 0): unknown[] | null => {
  if (Array.isArray(payload)) {
    return payload;
  }
  if (!payload || typeof payload !== 'object' || depth > 4) {
    return null;
  }
  const record = payload as Record<string, unknown>;
  const candidates = [record['_data_'], record._data_, record.data, record.prices, record.price, record.list, record.results];
  for (const candidate of candidates) {
    if (!candidate || candidate === payload) {
      continue;
    }
    const extracted = extractPricingArray(candidate, depth + 1);
    if (extracted) {
      return extracted;
    }
  }
  return null;
};

const normalizePricingTemplates = (payload: unknown): PricingTemplate[] => {
  const source = extractPricingArray(payload);
  if (!source) {
    return [];
  }
  return source.map((entry) => normalizePricingTemplate(entry)).filter((entry): entry is PricingTemplate => Boolean(entry));
};

const templateToPricing = (template: PricingTemplate): ParkingSpotPricing => ({
  charging_period: template.charging_period ?? undefined,
  passenger_cars: parsePriceNumber(template.passenger_cars) ?? undefined,
  trucks: parsePriceNumber(template.trucks) ?? undefined,
  buses: parsePriceNumber(template.buses) ?? undefined,
  moto_bikes: parsePriceNumber(template.moto_bikes) ?? undefined,
  campers: parsePriceNumber(template.campers) ?? undefined,
  charging_station: parsePriceNumber(template.charging_station) ?? undefined,
});

const fetchParkingPricing = async (token: string, templateId?: string | null): Promise<{ pricing: ParkingSpotPricing | null; label: string | null }> => {
  try {
    const response = await callParkingBoxApi(
      {
        action: 'get-parking-prices',
      },
      { jwtToken: token, useStoredToken: true }
    );
    const templates = normalizePricingTemplates(response.json);
    const selected = (templateId ? templates.find((entry) => entry.id === templateId) : null) || templates.find((entry) => entry.id) || templates[0] || null;
    if (!selected) {
      return { pricing: null, label: null };
    }
    return { pricing: templateToPricing(selected), label: selected.name };
  } catch (error) {
    console.error('[ParkingDetailPage] Nie udało się pobrać stawek:', error);
    return { pricing: null, label: null };
  }
};

const wrapRotation = (rotation: number) => {
  const wrapped = ((rotation % 360) + 360) % 360;
  return wrapped;
};

const parseLayoutNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const parseLayoutSize = (input: unknown): [number, number] | null => {
  if (Array.isArray(input) && input.length >= 2) {
    const w = parseLayoutNumber(input[0]);
    const h = parseLayoutNumber(input[1]);
    if (w !== null && h !== null) {
      return [w, h];
    }
  }
  if (input && typeof input === 'object') {
    const record = input as Record<string, unknown>;
    const w = parseLayoutNumber(record.width ?? record.w ?? record.sizeX ?? record.size_x);
    const h = parseLayoutNumber(record.height ?? record.h ?? record.sizeY ?? record.size_y);
    if (w !== null && h !== null) {
      return [w, h];
    }
  }
  if (typeof input === 'string') {
    const parts = input.split(/[,x; ]+/).filter(Boolean);
    if (parts.length >= 2) {
      const w = parseLayoutNumber(parts[0].replace(/^\[/, '').replace(/\]$/, ''));
      const h = parseLayoutNumber(parts[1].replace(/^\[/, '').replace(/\]$/, ''));
      if (w !== null && h !== null) {
        return [w, h];
      }
    }
    try {
      const parsed = JSON.parse(input) as unknown;
      if (Array.isArray(parsed) && parsed.length >= 2) {
        const w = parseLayoutNumber(parsed[0]);
        const h = parseLayoutNumber(parsed[1]);
        if (w !== null && h !== null) {
          return [w, h];
        }
      }
    } catch {
      // ignore parse errors
    }
  }
  return null;
};

const PARKING_SPOT_TYPES: ParkingSpotType[] = ['space_regular', 'space_handi', 'space_family', 'space_disabled', 'space_ev'];
const normalizeSpotTypeValue = (value: unknown): ParkingSpotType =>
  typeof value === 'string' && PARKING_SPOT_TYPES.includes(value.trim() as ParkingSpotType) ? (value.trim() as ParkingSpotType) : 'space_regular';
const LAYOUT_GATE_TYPES = ['entry', 'exit'] as const;
const normalizeLayoutGateType = (value: unknown): 'entry' | 'exit' | null =>
  typeof value === 'string' && LAYOUT_GATE_TYPES.includes(value.trim().toLowerCase() as 'entry' | 'exit')
    ? (value.trim().toLowerCase() as 'entry' | 'exit')
    : null;
const resolveLayoutObjectLabel = (record: Record<string, unknown>) => {
  const metaLabel =
    record.meta && typeof record.meta === 'object' && typeof (record.meta as Record<string, unknown>).label === 'string'
      ? ((record.meta as Record<string, unknown>).label as string)
      : null;
  const raw = [record.label, record.name, metaLabel].find((value): value is string => typeof value === 'string' && value.trim().length > 0);
  return raw?.trim() ?? '';
};
const resolveLayoutCenter = (record: Record<string, unknown>, width: number, height: number, topLeftFallback = false) => {
  let cx = parseLayoutNumber(record.cx ?? record.center_x ?? record.centerX);
  let cy = parseLayoutNumber(record.cy ?? record.center_y ?? record.centerY);
  const fallbackX = parseLayoutNumber(record.x);
  const fallbackY = parseLayoutNumber(record.y);
  if (cx === null && fallbackX !== null) {
    cx = topLeftFallback ? fallbackX + width / 2 : fallbackX;
  }
  if (cy === null && fallbackY !== null) {
    cy = topLeftFallback ? fallbackY + height / 2 : fallbackY;
  }
  if (cx === null || cy === null) {
    return null;
  }
  return { cx, cy };
};

const parseLayoutSpot = (entry: unknown, index: number, topLeftFallback = false): BuilderSpot | null => {
  if (!entry || typeof entry !== 'object') {
    return null;
  }
  const record = entry as Record<string, unknown>;
  const width = parseLayoutNumber(record.width ?? record.w);
  const height = parseLayoutNumber(record.height ?? record.h);
  if (width === null || height === null) {
    return null;
  }
  const center = resolveLayoutCenter(record, width, height, topLeftFallback);
  if (!center) {
    return null;
  }
  const id = typeof record.id === 'string' && record.id.trim().length > 0 ? record.id.trim() : `slot-${index + 1}`;
  const label = resolveLayoutObjectLabel(record) || `M${index + 1}`;
  const rotation = parseLayoutNumber(record.rotation ?? record.angle) ?? 0;
  const type = normalizeSpotTypeValue(record.type ?? (record.meta as Record<string, unknown> | undefined)?.type);
  return {
    id,
    label,
    cx: center.cx,
    cy: center.cy,
    width,
    height,
    rotation: wrapRotation(rotation),
    type,
  };
};

const parseLayoutSpotsArray = (value: unknown): BuilderSpot[] => {
  const resolveArray = (): unknown[] | null => {
    if (Array.isArray(value)) {
      return value;
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed.length) {
        return null;
      }
      try {
        const parsed = JSON.parse(trimmed) as unknown;
        if (Array.isArray(parsed)) {
          return parsed;
        }
      } catch {
        return null;
      }
    }
    return null;
  };

  const source = resolveArray();
  if (!source) {
    return [];
  }

  return source.map((entry, index) => parseLayoutSpot(entry, index)).filter((entry): entry is BuilderSpot => Boolean(entry));
};

const parseLayoutSpotsCollection = (value: unknown): Pick<ParkingLayoutBuilderValue, 'spots' | 'gates'> => {
  const resolveArray = (): unknown[] | null => {
    if (Array.isArray(value)) {
      return value;
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed.length) {
        return null;
      }
      try {
        const parsed = JSON.parse(trimmed) as unknown;
        if (Array.isArray(parsed)) {
          return parsed;
        }
      } catch {
        return null;
      }
    }
    return null;
  };

  const source = resolveArray();
  if (!source) {
    return { spots: [], gates: [] };
  }

  const spots: BuilderSpot[] = [];
  const gates: BuilderGate[] = [];
  const gateCounters: Record<'entry' | 'exit', number> = { entry: 0, exit: 0 };

  source.forEach((entry) => {
    if (!entry || typeof entry !== 'object') {
      return;
    }

    const record = entry as Record<string, unknown>;
    const gateType = normalizeLayoutGateType(record.type);
    if (gateType) {
      const parsedGate = parseLayoutGate(record, gateCounters[gateType], gateType);
      if (parsedGate) {
        gates.push(parsedGate);
        gateCounters[gateType] += 1;
      }
      return;
    }

    const parsedSpot = parseLayoutSpot(record, spots.length);
    if (parsedSpot) {
      spots.push(parsedSpot);
    }
  });

  return { spots, gates };
};

const parseLayoutGate = (entry: unknown, index: number, forcedType?: 'entry' | 'exit', topLeftFallback = false): BuilderGate | null => {
  if (!entry || typeof entry !== 'object') {
    return null;
  }
  const record = entry as Record<string, unknown>;
  const gateType = forcedType ?? normalizeLayoutGateType(record.type);
  if (!gateType) {
    return null;
  }
  const width = parseLayoutNumber(record.width ?? record.w) ?? 120;
  const height = parseLayoutNumber(record.height ?? record.h) ?? 42;
  const center = resolveLayoutCenter(record, width, height, topLeftFallback);
  if (!center) {
    return null;
  }
  const id = typeof record.id === 'string' && record.id.trim().length > 0 ? record.id.trim() : `${gateType}-${index + 1}`;
  const label = resolveLayoutObjectLabel(record) || `${gateType === 'entry' ? 'Wjazd' : 'Wyjazd'} ${index + 1}`;
  const rotation = parseLayoutNumber(record.rotation ?? record.angle) ?? 0;
  return {
    id,
    label,
    cx: center.cx,
    cy: center.cy,
    width,
    height,
    rotation: wrapRotation(rotation),
    type: gateType,
  };
};

const parseLayoutGatesArray = (value: unknown, forcedType?: 'entry' | 'exit'): BuilderGate[] => {
  const resolveArray = (): unknown[] | null => {
    if (Array.isArray(value)) {
      return value;
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed.length) {
        return null;
      }
      try {
        const parsed = JSON.parse(trimmed) as unknown;
        if (Array.isArray(parsed)) {
          return parsed;
        }
      } catch {
        return null;
      }
    }
    return null;
  };

  const source = resolveArray();
  if (!source) {
    return [];
  }

  return source.map((entry, index) => parseLayoutGate(entry, index, forcedType)).filter((entry): entry is BuilderGate => Boolean(entry));
};

const parseLayoutObjectsArray = (value: unknown): Pick<ParkingLayoutBuilderValue, 'spots' | 'gates'> => {
  const resolveArray = (): unknown[] | null => {
    if (Array.isArray(value)) {
      return value;
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed.length) {
        return null;
      }
      try {
        const parsed = JSON.parse(trimmed) as unknown;
        if (Array.isArray(parsed)) {
          return parsed;
        }
      } catch {
        return null;
      }
    }
    return null;
  };

  const source = resolveArray();
  if (!source) {
    return { spots: [], gates: [] };
  }

  const spots: BuilderSpot[] = [];
  const gates: BuilderGate[] = [];
  const gateCounters: Record<'entry' | 'exit', number> = { entry: 0, exit: 0 };

  source.forEach((entry) => {
    if (!entry || typeof entry !== 'object') {
      return;
    }
    const record = entry as Record<string, unknown>;
    const objectType = typeof record.type === 'string' ? record.type.trim().toLowerCase() : '';
    if (objectType === 'spot') {
      const parsed = parseLayoutSpot(record, spots.length, true);
      if (parsed) {
        spots.push(parsed);
      }
      return;
    }
    const gateType = normalizeLayoutGateType(objectType);
    if (!gateType) {
      return;
    }
    const parsed = parseLayoutGate(record, gateCounters[gateType], gateType, true);
    if (parsed) {
      gates.push(parsed);
      gateCounters[gateType] += 1;
    }
  });

  return { spots, gates };
};

const extractParkingSpotsRecord = (payload: unknown, depth = 0): Record<string, unknown> | null => {
  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    const record = payload as Record<string, unknown>;
    if (
      'spots' in record ||
      'Spots' in record ||
      'layout_spots' in record ||
      'slots' in record ||
      'objects' in record ||
      'layout_objects' in record ||
      'entries' in record ||
      'exits' in record ||
      'gates' in record
    ) {
      return record;
    }
    if (depth < 4) {
      for (const value of Object.values(record)) {
        if (!value || value === payload) {
          continue;
        }
        const extracted = extractParkingSpotsRecord(value, depth + 1);
        if (extracted) {
          return extracted;
        }
      }
    }
  }
  if (Array.isArray(payload) && depth < 4) {
    for (const value of payload) {
      const extracted = extractParkingSpotsRecord(value, depth + 1);
      if (extracted) {
        return extracted;
      }
    }
  }
  return null;
};

const normalizeParkingLayoutBuilderValue = (payload: unknown): ParkingLayoutBuilderValue | null => {
  const source = extractParkingSpotsRecord(payload);
  if (!source) {
    return null;
  }
  const size = parseLayoutSize(source.size ?? source.Size ?? source.layout_size ?? source.dimensions);
  if (!size) {
    return null;
  }
  const objectState = parseLayoutObjectsArray(source.objects ?? source.layout_objects);
  const mixedSpotsState = parseLayoutSpotsCollection(source.spots ?? source.Spots ?? source.layout_spots ?? source.slots);
  const parsedSpots =
    objectState.spots.length > 0
      ? objectState.spots
      : mixedSpotsState.spots.length > 0
        ? mixedSpotsState.spots
        : parseLayoutSpotsArray(source.spots ?? source.Spots ?? source.layout_spots ?? source.slots);
  const parsedGates =
    objectState.gates.length > 0
      ? objectState.gates
      : mixedSpotsState.gates.length > 0
        ? mixedSpotsState.gates
      : [
          ...parseLayoutGatesArray(source.entries ?? source.entry ?? source.entrances, 'entry'),
          ...parseLayoutGatesArray(source.exits ?? source.exit ?? source.outputs, 'exit'),
          ...parseLayoutGatesArray(source.gates ?? source.layout_gates),
        ];
  return {
    width: size[0],
    height: size[1],
    spots: parsedSpots,
    gates: parsedGates,
  };
};

const builderToLayoutRecord = (builder: ParkingLayoutBuilderValue, cityId: string, spotId: string): ParkingLayoutRecord => ({
  cityId,
  spotId,
  spotLabel: spotId,
  canvas: {
    width: Math.max(1, Math.round(builder.width)),
    height: Math.max(1, Math.round(builder.height)),
    gridSize: 5,
  },
  objects: [
    ...builder.spots.map((spot) => ({
      id: spot.id,
      type: 'spot' as const,
      x: spot.cx - spot.width / 2,
      y: spot.cy - spot.height / 2,
      width: spot.width,
      height: spot.height,
      rotation: wrapRotation(spot.rotation ?? 0),
      meta: { label: spot.label, type: normalizeSpotTypeValue(spot.type) },
    })),
    ...builder.gates.map((gate, index) => ({
      id: gate.id,
      type: gate.type,
      x: gate.cx - gate.width / 2,
      y: gate.cy - gate.height / 2,
      width: gate.width,
      height: gate.height,
      rotation: wrapRotation(gate.rotation ?? 0),
      meta: { label: gate.label.trim() || `${gate.type === 'entry' ? 'Wjazd' : 'Wyjazd'} ${index + 1}` },
    })),
  ],
  occupiedSlots: [],
  meta: {
    pattern: 'object-editor',
  },
});

type SpotOccupancyMap = Record<string, 0 | 1>;

type ParkingStats = {
  occupied: number;
  total: number;
  free: number;
  timestamp: string | null;
  spotOccupancy?: SpotOccupancyMap;
  cameraType?: CameraPlacement;
};

const parseStatNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const normalized = value.trim();
    if (!normalized.length) {
      return null;
    }
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const tryParseJson = (raw: string): unknown => {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const extractStatsTimestamp = (payload: unknown, depth = 0): string | null => {
  if (depth > 4 || payload === null || typeof payload === 'undefined') {
    return null;
  }
  if (typeof payload === 'string') {
    const trimmed = payload.trim();
    if (!trimmed.length) {
      return null;
    }
    const parsed = tryParseJson(trimmed);
    if (parsed && parsed !== payload) {
      const extracted = extractStatsTimestamp(parsed, depth + 1);
      if (extracted) {
        return extracted;
      }
    }
    return null;
  }
  if (Array.isArray(payload)) {
    for (const entry of payload) {
      const extracted = extractStatsTimestamp(entry, depth + 1);
      if (extracted) {
        return extracted;
      }
    }
    return null;
  }
  if (typeof payload !== 'object') {
    return null;
  }
  const record = payload as Record<string, unknown>;
  const candidates = [record.time, record.ctime, record.timestamp];
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim().length) {
      return candidate.trim();
    }
  }
  const nestedCandidates = [record._data_, record.data, record.results, record.result, record.payload, record.response, record.body];
  for (const nested of nestedCandidates) {
    const extracted = extractStatsTimestamp(nested, depth + 1);
    if (extracted) {
      return extracted;
    }
  }
  return null;
};

const hasStatsShape = (record: Record<string, unknown>): boolean =>
  'total_occupied' in record || 'total_available' in record || 'parking_detail' in record || 'parkingDetail' in record;

const extractStatsSource = (payload: unknown, depth = 0): Record<string, unknown> | null => {
  if (depth > 4 || payload === null || typeof payload === 'undefined') {
    return null;
  }
  if (typeof payload === 'string') {
    const parsed = tryParseJson(payload.trim());
    return parsed ? extractStatsSource(parsed, depth + 1) : null;
  }
  if (Array.isArray(payload)) {
    for (const entry of payload) {
      const extracted = extractStatsSource(entry, depth + 1);
      if (extracted) {
        return extracted;
      }
    }
    return null;
  }
  if (typeof payload !== 'object') {
    return null;
  }
  const record = payload as Record<string, unknown>;

  if (hasStatsShape(record)) {
    return record;
  }

  if ('results' in record) {
    const rawResults = record.results;
    const parsedResults = typeof rawResults === 'string' ? tryParseJson(rawResults) : rawResults;
    const extracted = extractStatsSource(parsedResults, depth + 1);
    if (extracted) {
      return extracted;
    }
  }

  const nestedCandidates = [record._data_, record.data, record.response, record.payload, record.body, record.result];
  for (const candidate of nestedCandidates) {
    const extracted = extractStatsSource(candidate, depth + 1);
    if (extracted) {
      return extracted;
    }
  }

  return null;
};

const extractParkingDetailArray = (record: Record<string, unknown>): Array<Record<string, unknown>> | null => {
  const detail = record.parking_detail ?? record.parkingDetail ?? record.detail ?? null;
  if (Array.isArray(detail)) {
    return detail.filter((entry): entry is Record<string, unknown> => Boolean(entry) && typeof entry === 'object');
  }
  return null;
};

const parseOccupancyArray = (value: unknown): { total: number; occupied: number } | null => {
  if (!Array.isArray(value)) {
    return null;
  }
  let total = 0;
  let occupied = 0;
  for (const entry of value) {
    const parsed = parseStatNumber(entry);
    if (parsed === null) {
      continue;
    }
    total += 1;
    if (parsed > 0) {
      occupied += 1;
    }
  }
  return total > 0 ? { total, occupied } : null;
};

const buildSpotOccupancyMap = (source: Record<string, unknown>): { occupancyMap: SpotOccupancyMap; occupiedCount: number; totalCount: number } => {
  const occupancyMap: SpotOccupancyMap = {};
  let occupiedCount = 0;
  let totalCount = 0;

  Object.entries(source).forEach(([key, value]) => {
    if (!key || typeof key !== 'string') {
      return;
    }
    const normalizedKey = key.toLowerCase();
    if (
      normalizedKey.startsWith('total_') ||
      normalizedKey === 'total' ||
      normalizedKey === 'free' ||
      normalizedKey === 'report_type' ||
      normalizedKey === 'reporttype' ||
      normalizedKey === 'timestamp' ||
      normalizedKey === 'time' ||
      normalizedKey === 'ctime'
    ) {
      return;
    }
    if (value && typeof value === 'object') {
      const record = value as Record<string, unknown>;
      const occupancy = parseStatNumber(record.occupancy ?? record.occupied ?? record.value ?? record.status);
      if (occupancy === null) {
        return;
      }
      const normalized = occupancy > 0 ? 1 : 0;
      occupancyMap[key] = normalized;
      totalCount += 1;
      occupiedCount += normalized;
    }
  });

  return { occupancyMap, occupiedCount, totalCount };
};

const normalizeExternalParkingStatsResponse = (payload: unknown): ParkingStats | null => {
  const source = extractStatsSource(payload);
  if (!source) {
    return null;
  }

  const totalOccupied = parseStatNumber(source.total_occupied ?? source.totalOccupied ?? source.occupied);
  const totalFree = parseStatNumber(source.total_available ?? source.totalAvailable ?? source.free ?? source.total);
  const { occupancyMap, occupiedCount, totalCount } = buildSpotOccupancyMap(source);

  const occupiedValue = totalOccupied ?? (totalCount > 0 ? occupiedCount : null);
  if (occupiedValue === null) {
    return null;
  }

  const freeValue = totalFree ?? (totalCount > 0 && occupiedValue !== null ? Math.max(totalCount - occupiedValue, 0) : null);

  const totalValue = freeValue !== null ? occupiedValue + freeValue : totalCount > 0 ? totalCount : occupiedValue;
  const total = Math.max(0, Math.round(totalValue));
  const occupied = Math.max(0, Math.round(occupiedValue));
  const free = Math.max(0, Math.round(freeValue !== null ? freeValue : total - occupied));
  const timestamp = extractStatsTimestamp(source) ?? extractStatsTimestamp(payload);

  return {
    occupied,
    total,
    free,
    timestamp,
    spotOccupancy: Object.keys(occupancyMap).length ? occupancyMap : undefined,
    cameraType: 'external',
  };
};

const normalizeParkingStatsResponse = (payload: unknown): ParkingStats | null => {
  const internalSnapshot = extractInternalParkingSnapshot(payload);
  if (internalSnapshot) {
    const occupiedValue = internalSnapshot.occupied ?? 0;
    const totalValue = internalSnapshot.total ?? occupiedValue;
    const total = Math.max(0, Math.round(totalValue));
    const occupied = Math.max(0, Math.round(occupiedValue));
    const free = Math.max(0, Math.round(internalSnapshot.free ?? total - occupied));
    return {
      occupied,
      total,
      free,
      timestamp: internalSnapshot.timestamp,
      spotOccupancy: Object.keys(internalSnapshot.spotOccupancy).length ? internalSnapshot.spotOccupancy : undefined,
      cameraType: 'internal',
    };
  }

  const source = extractStatsSource(payload);
  if (!source) {
    return null;
  }

  const totalOccupied = parseStatNumber(source.total_occupied ?? source.totalOccupied ?? source.occupied);
  const totalAvailable = parseStatNumber(source.total_available ?? source.totalAvailable ?? source.free);
  const detailArray = extractParkingDetailArray(source);

  let occupiedFromDetails: number | null = null;
  let totalFromDetails: number | null = null;

  if (detailArray) {
    let occupiedSum = 0;
    let slotsSum = 0;
    for (const detail of detailArray) {
      const occupancy = parseOccupancyArray((detail as Record<string, unknown>).occupancy);
      if (occupancy) {
        occupiedSum += occupancy.occupied;
        slotsSum += occupancy.total;
      }
    }
    if (slotsSum > 0) {
      occupiedFromDetails = occupiedSum;
      totalFromDetails = slotsSum;
    }
  }

  const occupiedValue = totalOccupied ?? occupiedFromDetails;
  if (occupiedValue === null) {
    return null;
  }

  let totalValue: number | null = null;
  if (totalAvailable !== null) {
    totalValue = occupiedValue + totalAvailable;
  } else if (totalFromDetails !== null) {
    totalValue = totalFromDetails;
  } else {
    totalValue = occupiedValue;
  }

  const total = Math.max(0, Math.round(totalValue));
  const occupied = Math.max(0, Math.round(occupiedValue));
  const free = Math.max(0, total - occupied);
  const timestamp = extractStatsTimestamp(source) ?? extractStatsTimestamp(payload);

  return { occupied, total, free, timestamp };
};
